import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, BookOpen, X } from "lucide-react";
import Pager, { usePager } from "@/components/Pager";

export default function AdminCourseReviewPage() {
  const [queue, setQueue] = useState([]);
  const pager = usePager(queue, 15);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [form, setForm] = useState({ decision: "approved", note: "", author_share_percent: 70 });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/courses/review-queue");
      setQueue(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startReview = (c, decision) => {
    setReviewing(c);
    setForm({
      decision,
      note: "",
      author_share_percent: c.author_share_percent != null ? c.author_share_percent : 70,
    });
  };

  const submit = async () => {
    if (form.decision === "rejected" && !form.note.trim()) {
      toast.error("Please add a rejection note for the author");
      return;
    }
    try {
      await api.post(`/admin/courses/${reviewing.id}/review`, form);
      toast.success(form.decision === "approved" ? "Course approved & published" : "Course rejected");
      setReviewing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-course-review-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">LMS REVIEW QUEUE</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Approve <em className="italic text-kb-primary">submitted courses</em></h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
        Authors submit courses here for final publishing. Set the per-course author share (default 70%) before approving.
      </p>

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        queue.length === 0 ? (
          <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">
            No courses awaiting review. Authors will appear here after submission.
          </div>
        ) : (
        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          {pager.pageItems.map((c) => (
            <article key={c.id} className="kb-card p-5" data-testid={`review-course-${c.id}`}>
              <div className="flex items-start gap-3">
                <BookOpen className="w-5 h-5 text-kb-primary shrink-0" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-lg text-kb-ink leading-tight">{c.title}</h3>
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-1">
                    By {c.owner_name} · {c.level} · {c.lessons_count || 0} lessons · NPR {c.price_npr?.toLocaleString() || 0}
                  </div>
                  {c.description && <p className="text-sm text-kb-ink-muted mt-3 line-clamp-3">{c.description}</p>}
                  <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-light mt-3">
                    Submitted {c.submitted_for_review_at ? new Date(c.submitted_for_review_at).toLocaleString() : "—"}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <button onClick={() => startReview(c, "approved")} className="kb-btn-primary text-sm flex-1 justify-center" data-testid={`review-${c.id}-approve`}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                </button>
                <button onClick={() => startReview(c, "rejected")} className="kb-btn-outline text-sm flex-1 justify-center text-rose-600 border-rose-200" data-testid={`review-${c.id}-reject`}>
                  <XCircle className="w-3.5 h-3.5" /> Reject
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {reviewing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setReviewing(null)}>
          <div className="bg-white rounded-sm shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="course-review-modal">
            <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
              <div>
                <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-primary">{form.decision === "approved" ? "APPROVE & PUBLISH" : "REJECT"}</div>
                <h3 className="font-serif text-lg text-kb-ink mt-1">{reviewing.title}</h3>
              </div>
              <button onClick={() => setReviewing(null)} className="text-kb-ink-muted"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-4">
              {form.decision === "approved" && (
                <div>
                  <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Author revenue share %</label>
                  <input type="number" min="0" max="100" step="1" className="kb-input text-sm w-full mt-1"
                    value={form.author_share_percent}
                    onChange={(e) => setForm({ ...form, author_share_percent: (e.target.value === "" ? "" : (parseFloat(e.target.value) || 0)) })}
                    data-testid="review-author-share-input" />
                  <p className="text-xs text-kb-ink-muted mt-1">Default 70% — adjust per course. Platform keeps the rest.</p>
                </div>
              )}
              <div>
                <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                  {form.decision === "approved" ? "Note to author (optional)" : "Reason for rejection *"}
                </label>
                <textarea rows={3} className="kb-input text-sm w-full mt-1 resize-none"
                  value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
                  data-testid="review-note-input" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex gap-2 justify-end">
              <button onClick={() => setReviewing(null)} className="kb-btn-outline text-sm">Cancel</button>
              <button onClick={submit} className={`text-sm px-4 py-2 rounded-sm text-white ${form.decision === "approved" ? "bg-kb-primary hover:bg-kb-hover" : "bg-rose-600 hover:bg-rose-700"}`} data-testid="review-submit-btn">
                Confirm {form.decision === "approved" ? "approval" : "rejection"}
              </button>
            </div>
          </div>
        </div>
      )}
      <Pager {...pager} label="courses" testId="admin-course-review-pager" />
    </div>
  );
}